import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Testimonials } from "@/components/Testimonials";
import { FOUNDER, LOGO_URL } from "@/lib/data";
import { useReveal } from "@/hooks/useReveal";

export default function AboutPage() {
  const ref = useReveal();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "Om LD Media",
    about: {
      "@type": "Organization",
      name: "LD Media",
      founder: { "@type": "Person", name: FOUNDER.name, alternateName: FOUNDER.alias },
      foundingDate: "2025",
      description:
        "LD Media er et dansk marketing bureau grundlagt af Abbas Khan, der hjælper virksomheder med AI-videoer, content creation, hjemmesider, automatisering og digitale marketingløsninger.",
    },
  };

  return (
    <>
      <SEO
        title="Om os"
        path="/om"
        description="Læs historien bag LD Media – grundlagt af Abbas Khan (Abbas_LD) i 2025. Vi hjælper virksomheder med AI-videoer, content creation, hjemmesider og digital marketing."
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Om LD Media"
        title="Et bureau bygget på"
        accent="resultater og vision."
        subtitle="LD Media er ikke et traditionelt bureau. Vi er et lille, fokuseret team der kombinerer creative, annoncering og content produktion i ét. Læs historien bag os herunder."
      />

      <section className="py-12 lg:py-16">
        <div ref={ref} className="reveal mx-auto max-w-4xl px-5 lg:px-10">
          <div className="rounded-3xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.04] p-7 lg:p-10 mb-12">
            <div className="flex items-center gap-5">
              <div className="h-16 w-16 rounded-2xl overflow-hidden ring-2 ring-[hsl(var(--accent))]/60 flex-shrink-0">
                <img src={LOGO_URL} alt="LD Media" className="h-full w-full object-cover" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-semibold">
                  Founded 2025
                </div>
                <div className="font-display text-2xl font-bold mt-1">{FOUNDER.name}</div>
                <div className="text-sm text-white/60">{FOUNDER.role}</div>
              </div>
            </div>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">
            Historien bag <span className="text-[hsl(var(--accent))]">LD Media</span>
          </h2>

          <div className="space-y-6 text-white/75 leading-relaxed text-base sm:text-lg">
            {FOUNDER.story.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-4">
            <Feature title="Kvalitet" desc="Hver leverance gennemgår vores eget kvalitetstjek før den udsendes." />
            <Feature title="Innovation" desc="Vi følger med på AI, nye platforme og det nyeste i digital marketing." />
            <Feature title="Resultater" desc="Vi måler succes på det der faktisk virker for din forretning." />
            <Feature title="Tæt dialog" desc="Du har direkte kontakt til os. Ingen mellemled, ingen ventetid." />
          </div>
        </div>
      </section>

      <Testimonials />
      <CTASection title="Lad os bygge noget sammen" />
    </>
  );
}

const Feature = ({ title, desc }) => (
  <div className="rounded-2xl border border-white/10 p-5">
    <div className="font-semibold text-white">{title}</div>
    <div className="mt-2 text-sm text-white/55">{desc}</div>
  </div>
);
